// @flow
/* eslint-disable react/no-multi-comp */

import React, { memo } from 'react';

import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import {
    Icon,
    IconArrowDown,
    IconArrowLeft,
    IconArrowRight,
    IconArrowUp,
    IconChat,
    IconContain,
    IconCover,
    IconParticipants,
    IconRaisedHand,
    IconZoomIn,
    IconZoomOut,
    PollIcon
} from '../../../base/icons';
import { connect } from '../../../base/redux';
import { getBrandingColorPalette } from '../../../base/styles';
import { ToolboxButtonWithIcon } from '../../../base/toolbox/components';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { ChatCounter } from '../../../chat';
import SelectLabel from '../../../conference/components/web/SelectLabel';
import TileViewCarousel from '../../../filmstrip/components/web/TileViewCarousel';
import PresentationWindowButton from '../../../presentation-window/components/web/PresentationWindowButton';
import ReactionsContainerButton from '../../../reactions/components/web/ReactionsContainerButtons';
import ClosedCaptionButton from '../../../subtitles/components/web/ClosedCaptionButton.web';
import { LAYOUTS } from '../../../video-layout';

import AudioSettingsButton from './AudioSettingsButton';
import HangupSettings from './HangupSettings';
import OverflowMenuButton from './OverflowMenuButton';
import OverflowMenuContent from './OverflowMenuContent';
import ScreenOverflowButton from './ScreenOverflowButton';
import ShareMenuContent from './ShareMenuContent';
import ToolbarButton from './ToolbarButton';
import { mapStateToProps } from './Toolbox';
import VideoSettingsButton from './VideoSettingsButton';

/* global interfaceConfig */

type Props = {

    // Layout/state
    _chatOpen: boolean,
    _isPollsTabFocused: boolean,
    _raisedHand: boolean,
    _overflowMenuVisible: boolean,
    _screensharing: boolean,
    _ShareMenuVisibility: boolean,
    _isPictureInPictureEnabled: boolean,
    _visibleButtons: Set<string>,
    _requestingCaptionsModal: boolean,
    _participantsPaneOpen: boolean,
    floatToolboxHidden: boolean,
    _isMobile: boolean,
    conferenceview: string,
    conferenceView: string,
    filmStripView: string,
    participantsLength: number,
    _carouselStartIndex: number,
    totalPages: number,
    _desktopSharingEnabled: boolean,
    _isLocalWhiteboard: boolean,
    _isLocalEtherpad: boolean,
    _objectFit: string,
    _showVideoMutedIndicator: boolean,

    // Share menu props
    _shareVideoState: boolean,
    _whiteBoardvisible: boolean,
    _livePadVisible: boolean,
    _screenShareVisible: boolean,
    _isOwnSharingVideo: boolean,
    _isModerator: boolean,
    _localParticipantID: string,
    _isWhiteBoardID: ?string,
    _isetherPadId: ?string,
    desktopApp: boolean,
    _desktopSharingDisabledTooltipKey: boolean,

    // Overflow menu props
    _clicknPledgeVisible: boolean,
    _donorBoxVisible: boolean,
    _feedbackConfigured: boolean,
    _geturl: string,
    clickAndPledgeUrl: string,
    _isVoiceCommandDisabled: boolean,
    _isGuest: boolean,
    _isVpaasMeeting: boolean,
    _embedMeeting: boolean,
    liveStreamVisible: boolean,
    RecordingVisible: boolean,
    vcVisible: boolean,
    giventToken: ?string,
    _GiventOwnerId: ?string,
    _clickandpledGUID: ?string,
    _conference: Object,
    _isRecordingRunning: boolean,
    _isLiveStreamRunning: boolean,

    // Callbacks
    onToggleRaiseHand: Function,
    onToggleChat: Function,
    onTogglePolls: Function,
    onToggleParticipantsPane: Function,
    onSetOverflowVisible: Function,
    onSetShareScreenOverflowVisible: Function,
    onCaptionsArrowClick: Function,
    onViewStripMenuChange: Function,
    onTotalPagesChange: Function,
    onToggleScreenshare: Function,
    onZoomIn: Function,
    onZoomOut: Function,
    onToggleObjectFit: Function,

    // Overflow callbacks
    onToggleProfile: Function,
    onOpenVideoQuality: Function,
    onOpenSpeakerStats: Function,
    onOpenEmbedMeeting: Function,
    onOpenFeedback: Function,
    onOpenKeyboardShortcuts: Function,
    onToggleDonorbox: Function,
    onRemoveDonorbox: Function,
    onToggleClickAndPledge: Function,
    onRemoveClickAndPledge: Function,
    onToggleVoiceCommands: Function,

    dispatch: Function,
    t: Function,
};

/**
 * Renders the conference sidebar (vertical float layout, left bar).
 * Contains: raise hand, reactions, chat, polls, captions, participants, overflow menu.
 */
const ConferenceSideBar = memo(({
    _requestingCaptionsModal,
    _raisedHand,
    _chatOpen,
    _overflowMenuVisible,
    _visibleButtons,
    _isPictureInPictureEnabled,
    _isPollsTabFocused,
    _participantsPaneOpen,
    conferenceview,
    filmStripView,
    conferenceView,
    dispatch,
    t,
    onToggleRaiseHand,
    onToggleChat,
    onTogglePolls,
    onToggleParticipantsPane,
    onSetOverflowVisible,
    onCaptionsArrowClick,

    // overflow props
    _clicknPledgeVisible,
    _donorBoxVisible,
    _feedbackConfigured,
    _screensharing,
    _geturl,
    clickAndPledgeUrl,
    _isVoiceCommandDisabled,
    _isModerator,
    _isGuest,
    _isVpaasMeeting,
    _embedMeeting,
    liveStreamVisible,
    RecordingVisible,
    desktopApp,
    giventToken,
    _GiventOwnerId,
    _clickandpledGUID,
    _localParticipantID,
    _conference,
    _isRecordingRunning,
    _isLiveStreamRunning,
    vcVisible,
    onToggleProfile,
    onOpenVideoQuality,
    onOpenSpeakerStats,
    floatToolboxHidden,
    onOpenEmbedMeeting,
    onOpenFeedback,
    onOpenKeyboardShortcuts,
    onToggleDonorbox,
    onRemoveDonorbox,
    onToggleClickAndPledge,
    onRemoveClickAndPledge,
    onToggleVoiceCommands
}: Props) => {
    const primaryColor = interfaceConfig?.applyMeetingSettings && getBrandingColorPalette(interfaceConfig)?.primary;
    const overflowMenuStyle = { background: `${primaryColor}` };

    const conferenceSideBarStyle = {
        display: conferenceview === 'float' && filmStripView === LAYOUTS.TILE_VIEW && !floatToolboxHidden ? 'none' : ''
    };

    const overflowMenuContent = (
        <OverflowMenuContent
            RecordingVisible = { RecordingVisible }
            _GiventOwnerId = { _GiventOwnerId }
            _clickandpledGUID = { _clickandpledGUID }
            _clicknPledgeVisible = { _clicknPledgeVisible }
            _conference = { _conference }
            _donorBoxVisible = { _donorBoxVisible }
            _embedMeeting = { _embedMeeting }
            _feedbackConfigured = { _feedbackConfigured }
            _geturl = { _geturl }
            _isGuest = { _isGuest }
            _isLiveStreamRunning = { _isLiveStreamRunning }
            _isModerator = { _isModerator }
            _isRecordingRunning = { _isRecordingRunning }
            _isVoiceCommandDisabled = { _isVoiceCommandDisabled }
            _isVpaasMeeting = { _isVpaasMeeting }
            _localParticipantID = { _localParticipantID }
            _screensharing = { _screensharing }
            _visibleButtons = { _visibleButtons }
            clickAndPledgeUrl = { clickAndPledgeUrl }
            desktopApp = { desktopApp }
            dispatch = { dispatch }
            giventToken = { giventToken }
            liveStreamVisible = { liveStreamVisible }
            onOpenEmbedMeeting = { onOpenEmbedMeeting }
            onOpenFeedback = { onOpenFeedback }
            onOpenKeyboardShortcuts = { onOpenKeyboardShortcuts }
            onOpenSpeakerStats = { onOpenSpeakerStats }
            onOpenVideoQuality = { onOpenVideoQuality }
            onRemoveClickAndPledge = { onRemoveClickAndPledge }
            onRemoveDonorbox = { onRemoveDonorbox }
            onToggleClickAndPledge = { onToggleClickAndPledge }
            onToggleDonorbox = { onToggleDonorbox }
            onToggleProfile = { onToggleProfile }
            onToggleVoiceCommands = { onToggleVoiceCommands }
            vcVisible = { vcVisible } />
    );


    return (
        <div
            className = 'conference-sidebar'
            style = { conferenceSideBarStyle }>
            {!_isPictureInPictureEnabled && _visibleButtons.has('closedcaptions') && (
                <div className = 'icon-btn'>
                    <Tooltip
                        content = { t('transcribing.OpenCloseCaptions') }
                        position = 'left'>
                        {_requestingCaptionsModal
                            ? <ClosedCaptionButton />
                            : (
                                <ToolboxButtonWithIcon
                                    icon = { _requestingCaptionsModal ? IconArrowDown : IconArrowUp }
                                    iconDisabled = { false }
                                    iconTooltip = { t('transcribing.OpenCloseCaptions') }
                                    isFloatView = { conferenceView === 'float' }
                                    onIconClick = { onCaptionsArrowClick }>
                                    <ClosedCaptionButton />
                                </ToolboxButtonWithIcon>
                            )}
                    </Tooltip>
                </div>
            )}
            {_visibleButtons.has('raisehand') && (
                <div className = 'icon-btn'>
                    <ToolbarButton
                        accessibilityLabel = { t('toolbar.accessibilityLabel.raiseHand') }
                        icon = { IconRaisedHand }
                        onClick = { onToggleRaiseHand }
                        position = 'left'
                        toggled = { _raisedHand }
                        tooltip = { t('toolbar.raiseHand') } />
                </div>
            )}
            {!_isPictureInPictureEnabled && _visibleButtons.has('reactions') && (
                <div className = 'icon-btn'>
                    <ReactionsContainerButton onClick = { onToggleRaiseHand } />
                </div>
            )}
            {_visibleButtons.has('chat') && (
                <div className = 'icon-btn'>
                    <div className = 'toolbar-button-with-badge'>
                        <ToolbarButton
                            accessibilityLabel = { t('toolbar.accessibilityLabel.chat') }
                            icon = { IconChat }
                            onClick = { onToggleChat }
                            position = 'left'
                            toggled = { _chatOpen && !_isPollsTabFocused }
                            tooltip = { t('toolbar.chat') } />
                        <ChatCounter />
                    </div>
                </div>
            )}
            {_visibleButtons.has('polls') && !_isPictureInPictureEnabled && (
                <div className = 'icon-btn'>
                    <ToolbarButton
                        accessibilityLabel = 'Polls Pane'
                        icon = { PollIcon }
                        onClick = { onTogglePolls }
                        position = 'left'
                        toggled = { _chatOpen && _isPollsTabFocused }
                        tooltip = 'Polls Pane' />
                </div>
            )}
            {!_isPictureInPictureEnabled && _visibleButtons.has('participants-pane') && (
                <div className = 'icon-btn'>
                    <ToolbarButton
                        accessibilityLabel = { t('toolbar.accessibilityLabel.participants') }
                        icon = { IconParticipants }
                        key = 'participants-pane'
                        onClick = { onToggleParticipantsPane }
                        position = 'left'
                        toggled = { _participantsPaneOpen }
                        tooltip = { t('toolbar.participants') } />
                </div>
            )}
            <div className = 'icon-btn'>
                {!_isPictureInPictureEnabled && (
                    <OverflowMenuButton
                        isOpen = { _overflowMenuVisible }
                        onVisibilityChange = { onSetOverflowVisible }
                        position = 'left'>
                        <ul
                            aria-label = { t('toolbar.accessibilityLabel.moreActionsMenu') }
                            className = 'overflow-menu'
                            style = { overflowMenuStyle }>
                            <div className = 'rows'>{overflowMenuContent}</div>
                        </ul>
                    </OverflowMenuButton>
                )}
            </div>
        </div>
    );
});

ConferenceSideBar.displayName = 'ConferenceSideBar';

const ConnectedConferenceSideBar = translate(connect(mapStateToProps)(ConferenceSideBar));

export { ConnectedConferenceSideBar as ConferenceSideBar };

/**
 * Renders the conference bottom bar (float layout, bottom bar).
 * Contains: audio, video, screen share, hangup, label selector, zoom controls, tile carousel.
 */
const ConferenceBottomBar = memo(({
    _isMobile,
    _showVideoMutedIndicator,
    _ShareMenuVisibility,
    _isLocalWhiteboard,
    _isLocalEtherpad,
    _visibleButtons,
    _screensharing,
    _objectFit,
    filmStripView,
    participantsLength,
    _carouselStartIndex,
    conferenceView,
    _desktopSharingEnabled,
    totalPages,
    dispatch,
    t,
    onSetShareScreenOverflowVisible,
    onViewStripMenuChange,
    onZoomIn,
    onZoomOut,
    onToggleObjectFit,
    participant,

    // Share menu props
    _shareVideoState,
    isCustomParticipantVisible,
    isCustomParticipantEveryone,
    _whiteBoardvisible,
    _livePadVisible,
    _screenShareVisible,
    _isOwnSharingVideo,
    _isModerator,
    _localParticipantID,
    _isWhiteBoardID,
    _isetherPadId,
    desktopApp,
    _desktopSharingDisabledTooltipKey,
    onToggleScreenshare
}: Props) => {
    const primaryColor = interfaceConfig?.applyMeetingSettings && getBrandingColorPalette(interfaceConfig)?.primary;
    const overflowMenuStyle = { background: `${primaryColor}` };

    const shareMenuContent = (
        <ShareMenuContent
            _desktopSharingDisabledTooltipKey = { _desktopSharingDisabledTooltipKey }
            _isLocalEtherpad = { _isLocalEtherpad }
            _isLocalWhiteboard = { _isLocalWhiteboard }
            _isModerator = { _isModerator }
            _isOwnSharingVideo = { _isOwnSharingVideo }
            _isWhiteBoardID = { _isWhiteBoardID }
            _isetherPadId = { _isetherPadId }
            _livePadVisible = { _livePadVisible }
            _localParticipantID = { _localParticipantID }
            _screenShareVisible = { _screenShareVisible }
            _screensharing = { _screensharing }
            _shareVideoState = { _shareVideoState }
            _whiteBoardvisible = { _whiteBoardvisible }
            desktopApp = { desktopApp }
            desktopSharingEnabled = { _desktopSharingEnabled }
            onToggleScreenshare = { onToggleScreenshare } />
    );

    const screenMenuToggled = _screensharing || _isLocalWhiteboard || _isLocalEtherpad || (_shareVideoState && _isOwnSharingVideo);
    const screenShareParticipant = participant?.fakeParticipant === 'SharedVideo' || isCustomParticipantVisible || isCustomParticipantEveryone || filmStripView === LAYOUTS.TILE_VIEW;

    return (
        <div className = 'conference-bottom-bar'>
            <div className = 'conference-bottom-bar-audio-video'>
                {_visibleButtons.has('microphone') && (
                    <div className = 'icon-btn'>
                        <AudioSettingsButton
                            dispatch = { dispatch }
                            key = 'asb'
                            visible = { true } />
                    </div>
                )}
                {_visibleButtons.has('camera') && (
                    <div className = 'icon-btn'>
                        <VideoSettingsButton
                            dispatch = { dispatch }
                            key = 'vsb'
                            visible = { true } />
                    </div>
                )}
            </div>
            {/* Screen share button — only shown on screens wider than 480px via CSS */}
            {_visibleButtons.has('desktop') && (!isMobileBrowser() || _isModerator) && (
                <div className = 'icon-btn screen-share-wide-only'>
                    <ScreenOverflowButton
                        disabledProps = { React.Children.toArray(shareMenuContent).filter(Boolean).length === 0 }
                        isOpen = { _ShareMenuVisibility }
                        onVisibilityChange = { onSetShareScreenOverflowVisible }
                        toggled = { screenMenuToggled }>
                        <ul
                            aria-label = { t('toolbar.accessibilityLabel.moreActionsMenu') }
                            className = 'overflow-menu'
                            style = { overflowMenuStyle }>
                            {shareMenuContent}
                        </ul>
                    </ScreenOverflowButton>
                </div>
            )}
            {_visibleButtons.has('hangup') && (
                <HangupSettings
                    customClass = 'hangup-button '
                    visible = { _visibleButtons.has('hangup') } />
            )}
            {[ 'tileview', 'filmstrip', 'horizontalstrip', 'stageview', 'stage' ].some(btn => _visibleButtons.has(btn)) && (
                <SelectLabel
                    _filmStripView = { filmStripView }
                    _participantsLength = { participantsLength }
                    _visibleButtons = { _visibleButtons }
                    conferenceView = { conferenceView }
                    dispatch = { dispatch }
                    onViewStripMenuChange = { onViewStripMenuChange }
                    screenSize = { _isMobile }
                    totalPages = { totalPages } />
            )}
            {!screenShareParticipant && !_showVideoMutedIndicator && (
                <>
                    {_visibleButtons.has('zoomin') && (
                        <Tooltip
                            content = 'Zoom In'
                            position = 'top'>
                            <button
                                className = 'toolbox-icon'
                                onClick = { onZoomIn }>
                                <Icon
                                    src = { IconZoomIn }
                                    width = { 20 } />
                            </button>
                        </Tooltip>
                    )}
                    {_visibleButtons.has('zoomout') && (
                        <Tooltip
                            content = 'Zoom Out'
                            position = 'top'>
                            <button
                                className = 'toolbox-icon'
                                onClick = { onZoomOut }>
                                <Icon
                                    src = { IconZoomOut }
                                    width = { 20 } />
                            </button>
                        </Tooltip>
                    )}
                    {_visibleButtons.has('fit-video') && !participant?.isFakeParticipant && (
                        <Tooltip
                            content = { _objectFit === 'cover' ? t('tooltip.switchToContain') : t('tooltip.switchToCover') }
                            position = 'top'>
                            <button
                                className = 'toolbox-icon'
                                onClick = { onToggleObjectFit }>
                                <Icon
                                    src = { _objectFit === 'cover' ? IconCover : IconContain }
                                    width = { 20 } />
                            </button>
                        </Tooltip>
                    )}
                    {participant?.isFakeParticipant && <PresentationWindowButton float = { true } />}
                </>
            )}
            <TileViewCarousel
                _startIndex = { _carouselStartIndex || 0 }
                _totalParticipants = { participantsLength } />
        </div>
    );
});

ConferenceBottomBar.displayName = 'ConferenceBottomBar';

/**
 * Combined FloatViewFooter — renders the sidebar and bottom bar
 * for the float layout view. Also handles mobile/tablet sidebar.
 */
const FloatViewFooter = memo((props: Props) => {
    const {
        floatToolboxHidden,
        _isPictureInPictureEnabled,
        _visibleButtons,
        filmStripView,
        onHandleConferenceSideBarView
    } = props;

    const ConferenceSideBarhoveredClass = `conference-sidebar-parent ${floatToolboxHidden ? 'visible' : ''}`;
    const ConferenceBottomBarhoveredClass = `conference-bottom-bar-parent ${floatToolboxHidden ? 'visible' : ''}`;
    const ConferenceBottomBarStyle = {
        visibility: `${floatToolboxHidden ? 'visible' : filmStripView === LAYOUTS.TILE_VIEW ? 'hidden' : 'visible'}`
    };

    return (
        <>
            {/* Sidebar */}
            {!_isPictureInPictureEnabled && (
                <div className = { ConferenceSideBarhoveredClass }>
                    <div
                        className = 'conference-sidebar-icon'
                        onClick = { onHandleConferenceSideBarView }>
                        <Icon src = { floatToolboxHidden ? IconArrowRight : IconArrowLeft } />
                    </div>
                    <ConferenceSideBar { ...props } />
                </div>
            )}
            {/* Bottom bar */}
            {!_isPictureInPictureEnabled && (
                <div
                    className = { ConferenceBottomBarhoveredClass }
                    style = { ConferenceBottomBarStyle }>
                    <div
                        className = 'conference-sidebar-icon'
                        onClick = { onHandleConferenceSideBarView }>
                        <Icon src = { floatToolboxHidden ? IconArrowDown : IconArrowUp } />
                    </div>
                    {[ 'hangup', 'chat', 'polls', 'microphone', 'camera', 'desktop', 'invite',
                        'horizontalstrip', 'tileview', 'filmstrip', 'stageview', 'stage' ].some(btn => _visibleButtons.has(btn))
                        && <ConferenceBottomBar { ...props } />}
                </div>
            )}
        </>
    );
});

FloatViewFooter.displayName = 'FloatViewFooter';

export { ConferenceBottomBar };
export default translate(connect(mapStateToProps)(FloatViewFooter));
